/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './ManufacturingFields.module.scss';

type Translate = (key: string) => string;

type Category = {
  number: string;
  title: string;
  items: string[];
};

const getCategories = (t: Translate): Category[] => [
  {
    number: '01',
    title: t('Company registry data'),
    items: [
      t('Legal name and previous names'),
      t('Registration number, VAT and tax identifiers'),
      t('Legal form and incorporation date'),
      t('Company status'),
      t('Registered and trading addresses'),
      t('Register of record and filing reference'),
    ],
  },
  {
    number: '02',
    title: t('Financials'),
    items: [
      t('Filed revenue and turnover'),
      t('Assets, liabilities and net assets'),
      t('Profit and loss figures'),
      t('Filing history and latest accounts'),
      t('Currency and reporting period'),
      t('Multi-year financial trend'),
    ],
  },
  {
    number: '03',
    title: t('Firmographics'),
    items: [
      t('Industry codes, SIC, NACE and NAICS'),
      t('Employee counts and size bands'),
      t('Company age and trading status'),
      t('Websites and digital footprint'),
      t('Business phone numbers'),
      t('Geographic and jurisdiction data'),
    ],
  },
  {
    number: '04',
    title: t('Shareholders & ownership'),
    items: [
      t('Shareholders and shareholdings'),
      t('Share classes and percentages'),
      t('Beneficial owners, PSC and UBO'),
      t('Ownership percentages'),
      t('Ultimate beneficial owner chain'),
    ],
  },
  {
    number: '05',
    title: t('Group structure'),
    items: [
      t('Immediate parent'),
      t('Global ultimate owner'),
      t('Subsidiaries and branches'),
      t('Corporate linkage across registers'),
      t('Cross-register entity resolution'),
    ],
  },
  {
    number: '06',
    title: t('Directors & officers'),
    items: [
      t('Directors and active officers'),
      t('Officer role and position'),
      t('Appointment and resignation dates'),
      t('Nationality and country of residence'),
      t('Date of birth where filed'),
      t('Occupation'),
    ],
  },
];

const FIELD_TOTAL = 147;

const FieldCount = () => {
  const ref = useRef<HTMLSpanElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.6 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!revealed) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setCount(FIELD_TOTAL);
      return;
    }

    const duration = 900;
    const start = performance.now();

    let frame: number;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setCount(Math.round(eased * FIELD_TOTAL));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [revealed]);

  return (
    <span className={styles.count} ref={ref}>
      {count}
    </span>
  );
};

export const ManufacturingFields = () => {
  const { t } = useInt();
  const gridRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(gridRef, { threshold: 0.1 });

  return (
    <section className={styles.root} id="fields">
      <div className={styles.wrap}>
        <div className={styles.head}>
          <TitleBlock
            title={t('What sits on a resolved supplier.')}
            description={t(
              'Six categories, each read from the register that holds it and carrying the source alongside the value. Availability varies by jurisdiction, because disclosure does.',
            )}
            align="left"
          />
        </div>

        <div className={cn(styles.categories, revealed && styles.categoriesOn)} ref={gridRef}>
          {getCategories(t).map((category, i) => (
            <div className={styles.category} style={{ '--d': `${i * 60}ms` } as CSSProperties} key={category.title}>
              <span className={styles.n}>{category.number}</span>
              <h3 className={styles.categoryTitle}>{category.title}</h3>
              <ul className={styles.list}>
                {category.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className={styles.foot}>
          <p className={styles.footNote}>
            <FieldCount />+ {t('documented fields per record across the full dictionary.')}
          </p>
          <Button type="text" size="lg" href="/data-dictionary" suffix="→">
            {t('Read the data dictionary')}
          </Button>
        </div>
      </div>
    </section>
  );
};
